import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  DirectionsCarFilledOutlined,
  FmdGoodOutlined,
  NotificationsActiveOutlined,
  PlayArrowRounded,
  ShieldOutlined,
  VideocamOutlined,
} from "@mui/icons-material";

const captainPlayStoreUrl =
  "https://play.google.com/store/apps/details?id=com.skuvahncaptain&hl=en_IN";
const guardianPlayStoreUrl =
  "https://play.google.com/store/apps/details?id=com.skuvahn&hl=en_IN";

const features = [
  {
    title: "Live GPS Tracking",
    description: "Track your child's cab or van throughout every journey.",
    icon: FmdGoodOutlined,
  },
  {
    title: "Live In-Vehicle Feed",
    description: "View a secure camera feed during active trips when enabled.",
    icon: VideocamOutlined,
  },
  {
    title: "Pickup & Drop Alerts",
    description: "Receive timely updates when your child is picked up and dropped off.",
    icon: NotificationsActiveOutlined,
  },
  {
    title: "Secure by Design",
    description: "Trip information is available only to authorised users.",
    icon: ShieldOutlined,
  },
];

interface AppDownloadCardProps {
  title: string;
  description: string;
  screenshot: string;
  qrCode: string;
  storeUrl?: string;
  storeLabel: string;
  comingSoon?: boolean;
}

const AppDownloadCard = ({
  title,
  description,
  screenshot,
  qrCode,
  storeUrl,
  storeLabel,
  comingSoon = false,
}: AppDownloadCardProps) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      border: "1px solid #dce6f2",
      borderRadius: 4,
      overflow: "hidden",
      backgroundColor: "#fff",
    }}
  >
    <Grid container sx={{ height: "100%" }}>
      <Grid item xs={5} sx={{ backgroundColor: "#edf6ff", p: 2 }}>
        <Box
          component="img"
          src={screenshot}
          alt={`${title} screenshot`}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: 290,
            maxHeight: 380,
            objectFit: "cover",
            objectPosition: "top",
            borderRadius: 2,
            boxShadow: "0 12px 30px rgba(15, 91, 167, 0.22)",
          }}
        />
      </Grid>
      <Grid item xs={7}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 }, height: "100%" }}>
          <Typography variant="h5" fontWeight={700} color="#102f4e">
            {title}
          </Typography>
          <Typography color="text.secondary" mt={1.25} minHeight={68}>
            {description}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" mt={2.5}>
            <Box
              component="img"
              src={qrCode}
              alt={`${title} QR code`}
              sx={{ width: 88, height: 88, borderRadius: 1 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Scan to download
              </Typography>
              <Button
                component={storeUrl ? "a" : "button"}
                href={storeUrl}
                target={storeUrl ? "_blank" : undefined}
                rel={storeUrl ? "noopener noreferrer" : undefined}
                variant="contained"
                disabled={comingSoon}
                startIcon={<PlayArrowRounded />}
                sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}
              >
                {storeLabel}
              </Button>
            </Box>
          </Stack>
          {comingSoon && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              The App Store link will be added soon.
            </Typography>
          )}
        </CardContent>
      </Grid>
    </Grid>
  </Card>
);

const LandingPage = () => (
  <Box sx={{ backgroundColor: "#fff", color: "#102f4e" }}>
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid #e7edf5",
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" py={1.25}>
          <Box component="img" src="/app_logo.jpg" alt="SkuVahn" sx={{ height: 46, width: "auto" }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button href="#features" color="inherit" sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Features
            </Button>
            <Button href="#apps" color="inherit" sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Apps
            </Button>
            <Button href="#contact" variant="contained" sx={{ borderRadius: 2, textTransform: "none" }}>
              Contact us
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>

    <Box
      component="section"
      sx={{
        overflow: "hidden",
        background: "linear-gradient(135deg, #eff8ff 0%, #ffffff 58%, #fff7df 100%)",
        py: { xs: 7, md: 11 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Chip label="Safer school journeys, every day" color="primary" sx={{ fontWeight: 700, mb: 2.5 }} />
            <Typography variant="h2" fontWeight={800} lineHeight={1.08} sx={{ fontSize: { xs: "2.6rem", md: "4.25rem" } }}>
              Peace of mind for every ride.
            </Typography>
            <Typography variant="h6" color="text.secondary" lineHeight={1.7} mt={3} maxWidth={670}>
              SkuVahn helps parents stay connected with their child&apos;s daily commute through live visibility, real-time updates, and secure access to every trip.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4}>
              <Button href="#apps" size="large" variant="contained" sx={{ borderRadius: 2, px: 3, textTransform: "none" }}>
                Explore our apps
              </Button>
              <Button href="#features" size="large" variant="outlined" sx={{ borderRadius: 2, px: 3, textTransform: "none" }}>
                See how it works
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ position: "relative", mx: "auto", maxWidth: 390 }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: "8% 0 0 10%",
                  backgroundColor: "#ffbf2f",
                  borderRadius: "50%",
                  filter: "blur(4px)",
                }}
              />
              <Box
                component="img"
                src="/assests/Android%20Guardian/4.png"
                alt="SkuVahn Guardian app active rides screen"
                sx={{ position: "relative", display: "block", width: "66%", mx: "auto", borderRadius: 5, boxShadow: "0 25px 60px rgba(16, 47, 78, 0.3)" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="features" py={{ xs: 7, md: 10 }}>
      <Container maxWidth="lg">
        <Typography color="primary" fontWeight={800} textTransform="uppercase" letterSpacing={1.2}>
          Built for connected journeys
        </Typography>
        <Typography variant="h3" fontWeight={800} mt={1}>
          Everything families need to stay informed.
        </Typography>
        <Grid container spacing={3} mt={2}>
          {features.map(({ title, description, icon: Icon }) => (
            <Grid item xs={12} sm={6} md={3} key={title}>
              <Card elevation={0} sx={{ height: "100%", border: "1px solid #e1eaf4", borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "inline-flex", p: 1.25, borderRadius: 2, backgroundColor: "#e8f4ff", color: "#0F5BA7" }}>
                    <Icon fontSize="large" />
                  </Box>
                  <Typography variant="h6" fontWeight={700} mt={2}>
                    {title}
                  </Typography>
                  <Typography color="text.secondary" mt={1} lineHeight={1.65}>
                    {description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box component="section" sx={{ backgroundColor: "#0d3154", color: "#fff", py: { xs: 7, md: 9 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={6}>
            <DirectionsCarFilledOutlined sx={{ fontSize: 48, color: "#ffbf2f" }} />
            <Typography variant="h3" fontWeight={800} mt={2}>
              One platform for parents and transport operators.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography color="#d5e6f5" lineHeight={1.8}>
              Built for private school cabs and vans, SkuVahn brings drivers, vehicles, routes, bookings, and student assignments together—while giving parents the live journey information that matters most.
            </Typography>
            <Typography color="#d5e6f5" lineHeight={1.8} mt={2}>
              From pickup to drop-off, every journey becomes smarter, more transparent, and more reassuring.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="apps" sx={{ backgroundColor: "#f5f8fc", py: { xs: 7, md: 10 } }}>
      <Container maxWidth="lg">
        <Typography color="primary" fontWeight={800} textTransform="uppercase" letterSpacing={1.2}>
          Download SkuVahn
        </Typography>
        <Typography variant="h3" fontWeight={800} mt={1}>
          The right app for every journey.
        </Typography>
        <Typography color="text.secondary" mt={1.5} maxWidth={670}>
          Scan a QR code with your phone or choose your app store to get started.
        </Typography>
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6}>
            <AppDownloadCard
              title="SkuVahn Captain"
              description="For drivers and captains to manage assigned bookings, routes, and daily trips."
              screenshot="/assests/Android%20Captain/4.png"
              qrCode="/assets/qr/captain-android.png"
              storeUrl={captainPlayStoreUrl}
              storeLabel="Get it on Google Play"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AppDownloadCard
              title="SkuVahn Guardian · Android"
              description="For parents to follow active rides, receive alerts, and access transport details."
              screenshot="/assests/Android%20Guardian/4.png"
              qrCode="/assets/qr/guardian-android.png"
              storeUrl={guardianPlayStoreUrl}
              storeLabel="Get it on Google Play"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AppDownloadCard
              title="SkuVahn Guardian · iOS"
              description="The Guardian experience is also being prepared for iPhone and iPad users."
              screenshot="/assests/iOS%20Guardian/image4.png"
              qrCode="/assets/qr/guardian-ios-coming-soon.png"
              storeLabel="App Store coming soon"
              comingSoon
            />
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="contact" py={{ xs: 7, md: 10 }}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid #dce6f2", backgroundColor: "#eff8ff" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h4" fontWeight={800}>Support</Typography>
                <Typography color="text.secondary" mt={1}>Need help with the SkuVahn app? Our support team is here for you.</Typography>
                <Typography fontWeight={700} mt={3}>SkuVahn Support</Typography>
                <Typography mt={1}>Phone: <Box component="a" href="tel:+918130302668" sx={{ color: "#0F5BA7", fontWeight: 700 }}>+91 81303 02668</Box></Typography>
                <Typography mt={0.5}>Email: <Box component="a" href="mailto:skuvahn@gmail.com" sx={{ color: "#0F5BA7", fontWeight: 700 }}>skuvahn@gmail.com</Box></Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid #dce6f2" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h4" fontWeight={800}>Contact us</Typography>
                <Typography color="text.secondary" mt={1}>For partnerships, transport-operator enquiries, and general questions.</Typography>
                <Typography fontWeight={700} mt={3}>SKUVAHN PRIVATE LIMITED</Typography>
                <Typography mt={1}>First Floor, Flat No. 447, PKT-4, Dwarka, Sector 6 Dwarka, New Delhi, South West Delhi, Delhi – 110075</Typography>
                <Typography mt={1}>Phone: <Box component="a" href="tel:+917506006503" sx={{ color: "#0F5BA7", fontWeight: 700 }}>+91 75060 06503</Box></Typography>
                <Typography mt={1}>Email: <Box component="a" href="mailto:skuvahn@gmail.com" sx={{ color: "#0F5BA7", fontWeight: 700 }}>skuvahn@gmail.com</Box></Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="footer" sx={{ backgroundColor: "#0d3154", color: "#d5e6f5", py: 3 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
          <Typography variant="body2">© 2026 SkuVahn. Safe journeys. Peaceful minds.</Typography>
          <Stack direction="row" spacing={2}>
            <Box component="a" href="/privacy-policy" sx={{ color: "inherit", fontSize: 14 }}>Privacy Policy</Box>
            <Box component="a" href="/delete-request" sx={{ color: "inherit", fontSize: 14 }}>Delete Request</Box>
          </Stack>
        </Stack>
      </Container>
    </Box>
  </Box>
);

export default LandingPage;
